import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Tag, RedDot } from "../components/ui-primitives";
import { CONTACT, OFFICES } from "../data/content";
import { MapPin, Phone, Mail } from "lucide-react";
import ClosingCTA from "../components/ClosingCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOURCES = ["Referral", "Search / Online", "Media partner", "Industry event", "Other"];

export default function ContactPage() {
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    phone_or_email: "",
    advertise_about: "",
    source: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.company || !form.phone_or_email) {
      toast.error("Please fill out all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent. We'll reply shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" style={{ background: "var(--color-surface)" }}>
      <Toaster position="top-center" richColors />

      <section style={{ paddingTop: 152, paddingBottom: 64 }}>
        <div className="di-container">
          <Tag>Get in Touch</Tag>
          <h1 className="display-lg mt-6">
            Let's talk.
          </h1>
          <p className="mt-8" style={{ color: "var(--color-on-surface-variant)", maxWidth: 640 }}>
            We're a team of 52 across seven Indian cities. Tell us what you need — we'll tell you if we can help, and how.
          </p>
        </div>
      </section>

      <section className="di-section" style={{ background: "var(--color-surface-container-low)" }}>
        <div className="di-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact card — navy */}
          <aside
            className="lg:col-span-5"
            style={{ background: "var(--color-primary)", color: "var(--color-on-primary)", padding: "var(--space-48)" }}
          >
            <div
              className="label-sm inline-flex items-center gap-2 mb-4"
              style={{ color: "rgba(249,249,248,0.45)" }}
            >
              <MapPin size={12} /> Head Office
            </div>
            <p className="body-lg mb-10" style={{ color: "rgba(249,249,248,0.85)" }}>
              {CONTACT.address}
            </p>

            <div
              className="label-sm inline-flex items-center gap-2 mb-3"
              style={{ color: "rgba(249,249,248,0.45)" }}
            >
              <Phone size={12} /> Phone
            </div>
            <div className="mb-8">
              <a
                data-testid="contact-phone-link"
                href={`tel:${CONTACT.phone}`}
                className="display-sm"
                style={{ color: "var(--color-on-primary)" }}
              >
                {CONTACT.phone}
              </a>
            </div>

            <div
              className="label-sm inline-flex items-center gap-2 mb-3"
              style={{ color: "rgba(249,249,248,0.45)" }}
            >
              <Mail size={12} /> Email
            </div>
            <div className="mb-10">
              <a
                data-testid="contact-email-link"
                href={`mailto:${CONTACT.email}`}
                className="headline-md"
                style={{ color: "var(--color-on-primary)", wordBreak: "break-all" }}
              >
                {CONTACT.email}
              </a>
            </div>

            <div className="label-sm mb-4" style={{ color: "rgba(249,249,248,0.45)" }}>
              Seven Offices Across India
            </div>
            <div className="flex flex-wrap gap-2">
              {OFFICES.map((o) => (
                <span
                  key={o.city}
                  className="label-sm"
                  style={{
                    color: "rgba(249,249,248,0.7)",
                    border: "1px solid rgba(249,249,248,0.18)",
                    padding: "6px 10px",
                    borderRadius: "var(--radius-sm)",
                  }}
                >
                  {o.city}{o.note ? " (HQ)" : ""}
                </span>
              ))}
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-form-success"
                style={{ background: "var(--color-surface-container-lowest)", padding: "var(--space-48)", textAlign: "center" }}
              >
                <RedDot className="mb-6" />
                <h3 className="headline-lg mb-4">Thanks for reaching out.</h3>
                <p style={{ color: "var(--color-on-surface-variant)", maxWidth: 440, margin: "0 auto" }}>
                  A member of our team will reply to you shortly. In the meantime, feel free to call us at {CONTACT.phone}.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                style={{ background: "var(--color-surface-container-lowest)", padding: "var(--space-48)" }}
                className="space-y-6"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="label-sm block mb-1" style={{ color: "var(--color-on-surface-variant)" }}>
                      Full Name *
                    </label>
                    <input
                      name="full_name"
                      value={form.full_name}
                      onChange={onChange}
                      data-testid="contact-input-full-name"
                      className="di-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="label-sm block mb-1" style={{ color: "var(--color-on-surface-variant)" }}>
                      Company *
                    </label>
                    <input
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      data-testid="contact-input-company"
                      className="di-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="label-sm block mb-1" style={{ color: "var(--color-on-surface-variant)" }}>
                    Phone or Email *
                  </label>
                  <input
                    name="phone_or_email"
                    value={form.phone_or_email}
                    onChange={onChange}
                    data-testid="contact-input-phone-or-email"
                    className="di-input"
                    required
                  />
                </div>

                <div>
                  <label className="label-sm block mb-1" style={{ color: "var(--color-on-surface-variant)" }}>
                    What are you looking to advertise?
                  </label>
                  <textarea
                    name="advertise_about"
                    value={form.advertise_about}
                    onChange={onChange}
                    data-testid="contact-input-advertise"
                    rows={2}
                    className="di-input"
                    style={{ resize: "none" }}
                  />
                </div>

                <div>
                  <label className="label-sm block mb-1" style={{ color: "var(--color-on-surface-variant)" }}>
                    How did you hear about us?
                  </label>
                  <select
                    name="source"
                    value={form.source}
                    onChange={onChange}
                    data-testid="contact-input-source"
                    className="di-input"
                    style={{ appearance: "auto" }}
                  >
                    <option value="">Select (optional)</option>
                    {SOURCES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="btn btn-primary"
                  style={{ marginTop: 16 }}
                >
                  {loading ? "Sending…" : "Send →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
      <ClosingCTA showCta={false} />
    </main>
  );
}
